import { useEffect, useMemo, useState } from "react";
import {
  CARRIER_ROUTES,
  CARRIER_ROUTE_LABELS,
  type Carrier,
  type CarrierRoute,
} from "@aam/shared";
import { getSessionToken } from "../../hooks/useSession.js";
import { Button } from "../ui/Button.js";
import { PageHeader } from "../ui/PageHeader.js";
import { DataTableSkeleton, Input, Select } from "../ui/index.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface CarrierSettingView {
  carrier: Carrier;
  name: string;
  route: CarrierRoute;
  defaultRoute: CarrierRoute;
  reaganLabel: string;
  reaganLabelSaved: boolean;
  updatedAt: string | null;
}

interface OrganizationAccess {
  id: string;
  name: string;
  brandName: string;
  parentId: string | null;
  eligibleCarriers: string[];
}

const HEADERS = ["Carrier", "Goes to", "Name in Reagan.AI"];

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.success) throw new Error(json?.error || fallback);
  return json.data as T;
}

/**
 * /dashboard/carriers — the one place to see every carrier, where its
 * contracting goes after an agent requests it, and which organizations may
 * offer it. An organization with no carriers ticked offers the full catalog.
 */
export function CarrierManagement() {
  const token = getSessionToken();
  const [carriers, setCarriers] = useState<CarrierSettingView[] | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationAccess[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setError(null);
    void fetch(`${API_BASE}/api/carrier-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) =>
        readJson<{ carriers: CarrierSettingView[]; organizations: OrganizationAccess[] }>(
          response,
          "Unable to load carriers"
        )
      )
      .then((data) => {
        if (cancelled) return;
        setCarriers(data.carriers);
        setOrganizations(data.organizations);
        setLabels(Object.fromEntries(data.carriers.map((row) => [row.carrier, row.reaganLabel])));
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load carriers");
        setCarriers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, reloadToken]);

  // Organization lists hold form slugs (`aflac`), the lowercase enum value.
  const allCarrierValues = useMemo(() => (carriers ?? []).map((row) => row.carrier.toLowerCase()), [carriers]);

  async function saveCarrier(row: CarrierSettingView, route: CarrierRoute, reaganLabel: string) {
    if (!token) return;
    setSaving(`carrier:${row.carrier}`);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/carrier-settings/${row.carrier}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        // Only keep a Reagan name someone typed; the default stays computed.
        body: JSON.stringify({
          route,
          reaganLabel: reaganLabel.trim() && (row.reaganLabelSaved || reaganLabel.trim() !== row.reaganLabel)
            ? reaganLabel.trim()
            : null,
        }),
      });
      const saved = await readJson<CarrierSettingView>(response, "Unable to save carrier");
      setCarriers((current) => (current ?? []).map((entry) => (entry.carrier === saved.carrier ? saved : entry)));
      setLabels((current) => ({ ...current, [saved.carrier]: saved.reaganLabel }));
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save carrier");
    } finally {
      setSaving(null);
    }
  }

  async function toggleAccess(organization: OrganizationAccess, carrier: string) {
    if (!token) return;
    const current = organization.eligibleCarriers.length ? organization.eligibleCarriers : allCarrierValues;
    let next = current.includes(carrier) ? current.filter((value) => value !== carrier) : [...current, carrier];
    if (next.length === 0) {
      setError(`${organization.name} must offer at least one carrier. Tick another first.`);
      return;
    }
    // Every carrier ticked is stored as "no limit" so new carriers show up too.
    if (allCarrierValues.every((value) => next.includes(value))) next = [];
    setSaving(`org:${organization.id}`);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/organizations/${organization.id}/carrier-eligibility`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ carriers: next }),
      });
      const saved = await readJson<{ id: string; eligibleCarriers: string[] }>(
        response,
        "Unable to update organization carriers"
      );
      setOrganizations((list) =>
        list.map((entry) => (entry.id === saved.id ? { ...entry, eligibleCarriers: saved.eligibleCarriers } : entry))
      );
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update organization carriers");
    } finally {
      setSaving(null);
    }
  }

  const headers = [...HEADERS, ...organizations.map((organization) => organization.name)];

  return (
    <section className="space-y-6">
      <PageHeader
        title="Carrier management"
        description="Where each carrier's contracting goes after an agent requests it, and which organizations can offer it. An organization with every box ticked has no limit."
        actions={
          <Button variant="secondary" size="sm" onClick={() => setReloadToken((value) => value + 1)}>
            Refresh
          </Button>
        }
      />

      {error && (
        <p
          role="alert"
          className="rounded-card border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm text-status-rejected-text"
        >
          {error}
        </p>
      )}

      {carriers === null ? (
        <DataTableSkeleton
          headers={HEADERS}
          rows={10}
          cellVariants={["text", "short", "text"]}
          loadingLabel="Loading carriers"
        />
      ) : (
        <div className="overflow-x-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised shadow-card">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-overlay text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-content-muted">
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={header}
                    scope="col"
                    className={`whitespace-nowrap px-4 py-3 ${index >= HEADERS.length ? "text-center" : ""}`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {carriers.map((row) => {
                const busy = saving === `carrier:${row.carrier}`;
                const label = labels[row.carrier] ?? row.reaganLabel;
                return (
                  <tr key={row.carrier} className="align-middle">
                    <th scope="row" className="whitespace-nowrap px-4 py-3 text-left font-semibold text-content-primary">
                      {row.name}
                      {row.route !== row.defaultRoute && (
                        <span className="ml-2 rounded-md border border-status-pending-border bg-status-pending-bg px-1.5 py-0.5 text-[11px] font-semibold text-status-pending-text">
                          Changed
                        </span>
                      )}
                    </th>
                    <td className="px-4 py-2">
                      <Select
                        aria-label={`Where ${row.name} goes`}
                        value={row.route}
                        disabled={busy}
                        onChange={(event) => void saveCarrier(row, event.target.value as CarrierRoute, label)}
                        className="min-w-[16rem]"
                      >
                        {CARRIER_ROUTES.map((route) => (
                          <option key={route} value={route}>
                            {CARRIER_ROUTE_LABELS[route]}
                            {route === row.defaultRoute ? " (default)" : ""}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="px-4 py-2">
                      {row.route === "REAGAN" ? (
                        <Input
                          aria-label={`${row.name} name in the Reagan.AI dropdown`}
                          value={label}
                          disabled={busy}
                          onChange={(event) =>
                            setLabels((current) => ({ ...current, [row.carrier]: event.target.value }))
                          }
                          onBlur={() => {
                            if (label.trim() !== row.reaganLabel) void saveCarrier(row, row.route, label);
                          }}
                          className="min-w-[12rem]"
                        />
                      ) : (
                        <span className="text-content-muted">—</span>
                      )}
                    </td>
                    {organizations.map((organization) => {
                      const offered =
                        organization.eligibleCarriers.length === 0 ||
                        organization.eligibleCarriers.includes(row.carrier.toLowerCase());
                      return (
                        <td key={organization.id} className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[var(--color-accent-strong)]"
                            aria-label={`${organization.name} offers ${row.name}`}
                            checked={offered}
                            disabled={saving === `org:${organization.id}`}
                            onChange={() => void toggleAccess(organization, row.carrier.toLowerCase())}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs leading-5 text-content-secondary">
        Reagan.AI names must match the Contract Agent dropdown exactly, or the
        bot skips that carrier. Agents in Ark Life, or under an Ark Life upline,
        only ever see the Ark Life list.
      </p>
    </section>
  );
}
